/**
 * Alert Service
 *
 * Frontend service for managing per-checkout recipient alert configurations
 * and querying the notification log timeline.
 *
 * @module lib/alertService
 */
import { supabase } from '@/lib/supabaseClient'
import { createLogger } from '@/lib/logger'

const log = createLogger('AlertService')

/** Alert events that can be configured for checkout recipients (consignees).
 * This is a subset — recipients don't need internal/warehouse events. */
export const RECIPIENT_ALERT_EVENTS = [
  { key: 'carrier_accepted', label: 'Carrier Accepted', default: true },
  { key: 'in_transit', label: 'In Transit', default: false },
  { key: 'out_for_delivery', label: 'Out for Delivery', default: true },
  { key: 'delivered_to_borrower', label: 'Delivery Confirmation', default: true },
  { key: 'return_upcoming', label: 'Return Due Soon', default: true },
  { key: 'return_overdue', label: 'Return Overdue Notice', default: false },
] as const

export type RecipientAlertEvent = (typeof RECIPIENT_ALERT_EVENTS)[number]['key']

export interface CheckoutAlertRecipient {
  id?: number
  checkout_id: number
  contact_id: number | null
  recipient_email: string | null
  recipient_phone: string | null
  recipient_name: string | null
  alert_events: string[]
  channel_email: boolean
  channel_sms: boolean
  created_by?: string | null
}

export interface NotificationLogEntry {
  id: number
  checkout_id: number | null
  alert_id: number | null
  alert_key: string
  recipient_email: string | null
  recipient_phone: string | null
  recipient_name: string | null
  channel: 'email' | 'sms'
  status: 'pending' | 'sent' | 'delivered' | 'failed'
  sent_at: string | null
  outbox_id: number | null
  metadata: Record<string, unknown>
  created_at: string
}

/**
 * Load recipient alert config for a checkout.
 */
export async function getCheckoutRecipients(
  checkoutId: number,
): Promise<CheckoutAlertRecipient[]> {
  const { data, error } = await supabase
    .schema('app')
    .from('checkout_alert_recipients')
    .select('*')
    .eq('checkout_id', checkoutId)
    .order('created_at', { ascending: true })

  if (error) {
    log.error('Failed to load checkout recipients', error)
    throw error
  }

  return (data ?? []).map((row: any) => ({
    id: row.id,
    checkout_id: row.checkout_id,
    contact_id: row.contact_id,
    recipient_email: row.recipient_email,
    recipient_phone: row.recipient_phone,
    recipient_name: row.recipient_name,
    alert_events: (row.alert_events as string[]) ?? [],
    channel_email: row.channel_email ?? true,
    channel_sms: row.channel_sms ?? false,
    created_by: row.created_by,
  }))
}

/**
 * Save (upsert) recipient alert config for a checkout.
 * Uses checkout_id + contact_id unique constraint.
 */
export async function saveCheckoutRecipient(
  recipient: CheckoutAlertRecipient,
): Promise<CheckoutAlertRecipient> {
  const row = {
    checkout_id: recipient.checkout_id,
    contact_id: recipient.contact_id,
    recipient_email: recipient.recipient_email,
    recipient_phone: recipient.recipient_phone,
    recipient_name: recipient.recipient_name,
    alert_events: recipient.alert_events,
    channel_email: recipient.channel_email,
    channel_sms: recipient.channel_sms,
    created_by: recipient.created_by ?? null,
    updated_at: new Date().toISOString(),
  }

  const { data, error } = await supabase
    .schema('app')
    .from('checkout_alert_recipients')
    .upsert(row, { onConflict: 'checkout_id,contact_id' })
    .select('*')
    .maybeSingle()

  if (error) {
    log.error('Failed to save checkout recipient', error)
    throw error
  }

  return {
    id: data?.id,
    checkout_id: data?.checkout_id,
    contact_id: data?.contact_id,
    recipient_email: data?.recipient_email,
    recipient_phone: data?.recipient_phone,
    recipient_name: data?.recipient_name,
    alert_events: (data?.alert_events as string[]) ?? [],
    channel_email: data?.channel_email ?? true,
    channel_sms: data?.channel_sms ?? false,
    created_by: data?.created_by,
  }
}

/**
 * Delete a recipient alert config.
 */
export async function deleteCheckoutRecipient(id: number): Promise<void> {
  const { error } = await supabase
    .schema('app')
    .from('checkout_alert_recipients')
    .delete()
    .eq('id', id)

  if (error) {
    log.error('Failed to delete checkout recipient', error)
    throw error
  }
}

/**
 * Load the notification log timeline for a checkout.
 * Returns entries sorted newest first.
 */
export async function getNotificationLog(
  checkoutId: number,
  limit = 50,
): Promise<NotificationLogEntry[]> {
  const { data, error } = await supabase
    .schema('app')
    .from('notification_log')
    .select('*')
    .eq('checkout_id', checkoutId)
    .order('created_at', { ascending: false })
    .limit(limit)

  if (error) {
    log.error('Failed to load notification log', error)
    throw error
  }

  return (data ?? []) as NotificationLogEntry[]
}

/**
 * Load notification log entries for multiple checkouts (for admin overview).
 */
export async function getRecentNotifications(limit = 25): Promise<NotificationLogEntry[]> {
  const { data, error } = await supabase
    .schema('app')
    .from('notification_log')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit)

  if (error) {
    log.error('Failed to load recent notifications', error)
    throw error
  }

  return (data ?? []) as NotificationLogEntry[]
}

// ────────────────────────────────────────────────────
// Inbound Email / Reply Thread
// ────────────────────────────────────────────────────

/** Represents an inbound email from a checkout recipient. */
export interface InboxEntry {
  id: number
  from_email: string
  from_name: string | null
  to_address: string
  subject: string | null
  body_text: string | null
  body_html: string | null
  checkout_id: number | null
  contact_id: number | null
  in_reply_to: number | null
  is_read: boolean
  parsed_at: string | null
  created_at: string
}

/**
 * Load inbound emails for a checkout, newest first.
 */
export async function getInboxEntries(
  checkoutId: number,
  limit = 50,
): Promise<InboxEntry[]> {
  const { data, error } = await supabase
    .schema('app')
    .from('email_inbox')
    .select('*')
    .eq('checkout_id', checkoutId)
    .order('created_at', { ascending: false })
    .limit(limit)

  if (error) {
    log.error('Failed to load inbox entries', error)
    throw error
  }

  return (data ?? []) as InboxEntry[]
}

/**
 * Count unread inbound emails for a checkout.
 */
export async function getUnreadCount(checkoutId: number): Promise<number> {
  const { count, error } = await supabase
    .schema('app')
    .from('email_inbox')
    .select('id', { count: 'exact', head: true })
    .eq('checkout_id', checkoutId)
    .eq('is_read', false)

  if (error) {
    log.error('Failed to count unread', error)
    return 0
  }

  return count ?? 0
}

/**
 * Mark an inbox entry as read.
 */
export async function markInboxRead(inboxId: number): Promise<void> {
  const { error } = await supabase
    .schema('app')
    .from('email_inbox')
    .update({ is_read: true })
    .eq('id', inboxId)

  if (error) {
    log.error('Failed to mark inbox entry as read', error)
    throw error
  }
}

/**
 * Send a reply to a recipient via the email-reply edge function.
 */
export async function sendReply(params: {
  checkout_id: number
  contact_id: number | null
  inbox_id: number | null
  to_email: string
  to_name: string | null
  subject: string | null
  body_text: string
  sender_name: string | null
}): Promise<{ ok: boolean; outbox_id?: number }> {
  const { data, error } = await supabase.functions.invoke('email-reply', {
    body: params,
  })

  if (error) {
    log.error('Failed to send reply', error)
    throw error
  }

  return data as { ok: boolean; outbox_id?: number }
}
